"use client";

import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsx } from 'react/jsx-runtime';
import { useViewport } from '@vds/core';
import Button from '../button/index.js';
import TextLink from '../text-link/index.js';
import TextLinkCaret from '../text-link-caret/index.js';
import styles from './button-group.module.scss.js';
import { getHorizontalSpacing, getButtonKey, getVerticalSpacing } from './utils.js';

const chunk = (arr, size) => Array.from({ length: Math.ceil(arr.length / size) }, (v, i) => arr.slice(i * size, i * size + size));
const _renderButton = (_a, idx) => {
    var { kind } = _a, rest = __rest(_a, ["kind"]);
    const key = `${idx}-${kind}`;
    switch (kind.toLowerCase()) {
        case 'textlinkcaret':
            return (jsx(TextLinkCaret, Object.assign({}, rest, { size: "small" }), key));
        case 'textlink':
            return (jsx(TextLink, Object.assign({ kind: "standalone", wrapText: false }, rest), key));
        case 'primary':
        case 'secondary':
        case 'brandHighlight':
        default:
            return (jsx(Button, Object.assign({ kind: kind }, rest), key));
    }
};
const _cleanItemsData = (items) => {
    const containsTLC = items.some((d) => d.kind.toLowerCase() === 'textlinkcaret');
    let initialSize;
    return items.map((bgItem) => {
        const newItem = bgItem;
        if (newItem.kind.toLowerCase() !== 'textlinkcaret') {
            // If no size defined, or array contains TextLinkCaret, cast size to 'large'
            if (!newItem.size || containsTLC) {
                newItem.size = 'large';
            }
            else if (initialSize === undefined) {
                initialSize = newItem.size;
            }
            else if (initialSize !== newItem.size) {
                // If size is not the same as initialSize, cast next item to the initialSize
                newItem.size = initialSize;
            }
        }
        return newItem;
    });
};
const _getButtonKeys = ({ current, next, }) => {
    /* If no current button type, return */
    /* istanbul ignore if -- @preserve */
    if (!(current === null || current === void 0 ? void 0 : current.kind))
        return undefined;
    const currentUseKey = getButtonKey(current);
    const nextUseKey = next ? getButtonKey(next) : undefined;
    return { currentKey: currentUseKey, nextKey: nextUseKey || undefined };
};
const _getButtonWrapperStyles = ({ current, next, singleCta, singleRow, }) => {
    const bottomStyle = styles[`bottom${getVerticalSpacing(current, next || current)}`];
    const rightStyle = styles[`right${getHorizontalSpacing(current, next)}`];
    /* return if there's no current button type */
    /* istanbul ignore if -- @preserve */
    if (!current)
        return '';
    /* If no rowQuantity was passed and if there's a next button,
    set horizontal & vertical padding on the buttons
    */
    if (singleRow && next) {
        return [bottomStyle, rightStyle].join(' ').trim();
    }
    /* If no rowQuantity was passed and if there's no next button,
    set vertical padding on the buttons
    */
    if (singleRow && !singleCta) {
        return bottomStyle;
    }
    /* If no next button type and rowQuantity was passed, return */
    /* istanbul ignore next line -- @preserve */
    return !next ? '' : rightStyle;
};
/* istanbul ignore next line -- @preserve */
const _getRowWrapperStyles = (chunks) => {
    const _rankedButtonHeights = {
        textlinksmall: 0,
        textlinklarge: 1,
        textlinkcaret: 1,
        buttonsmall: 2,
        buttonlarge: 3, // 44px
    };
    const tallestButtons = chunks.map((buttonChunk) => {
        const initialKey = getButtonKey(buttonChunk[0]);
        // get the tallest item in a row chunk
        const tallestButtonInRow = buttonChunk.reduce((prev, curr) => {
            const currKey = getButtonKey(curr);
            const initialSize = _rankedButtonHeights[prev];
            return _rankedButtonHeights[currKey] > initialSize ? currKey : prev;
        }, initialKey);
        return tallestButtonInRow;
    });
    // determine padding between row using a map of rankedButtonHeight keys
    return styles[`bottom${getVerticalSpacing(tallestButtons[0], tallestButtons[1])}`];
};
const ButtonGroup = ({ items, alignment = 'center', rowQuantity, surface = 'lightPrimary', buttonWidth = 'auto', width, viewport: viewportProp, }) => {
    const isLargeButtonPresentInItems = items.some((item) => (item.kind === 'primary' ||
        item.kind === 'secondary' ||
        item.kind === 'brandHighlight') &&
        item.size === 'large');
    const containerStyles = [
        styles.buttonGroupWrapper,
        rowQuantity ? '' : styles.autoLayout,
        !rowQuantity && isLargeButtonPresentInItems ? styles.large : '',
    ];
    const viewport = useViewport('expanded', viewportProp);
    const cleanedItems = _cleanItemsData(items);
    const chunkSize = rowQuantity && rowQuantity[viewport]
        ? rowQuantity[viewport]
        : cleanedItems.length;
    const chunks = chunk(cleanedItems, chunkSize);
    const rowStyles = [styles.row, alignment ? styles[`${alignment}Align`] : ''];
    return (jsx("div", { className: containerStyles.join(' ').trim(), style: {
            width,
        }, children: chunks.map((row, chunkIdx) => {
            const nextRow = chunks[chunkIdx + 1];
            const rowGapStyles = nextRow
                ? _getRowWrapperStyles([row, nextRow])
                : '';
            return (jsx("div", { className: [rowGapStyles, ...rowStyles].join(' ').trim(), children: row.map((buttonGroupItem, idx) => {
                    const current = buttonGroupItem;
                    const next = row[idx + 1];
                    const singleCta = row.length === 1;
                    const singleRow = chunks.length === 1;
                    const buttonItemWrapperStyles = [styles.buttonWrapper];
                    const isButton = [
                        'primary',
                        'secondary',
                        'brandHighlight',
                    ].includes(buttonGroupItem.kind);
                    let rightSpacing = 0;
                    const buttonKeys = _getButtonKeys({ current, next });
                    if ((next || singleRow) && buttonKeys) {
                        const { currentKey, nextKey } = buttonKeys;
                        const spacingStyles = _getButtonWrapperStyles({
                            current: currentKey,
                            next: nextKey,
                            singleCta,
                            singleRow,
                        });
                        buttonItemWrapperStyles.push(spacingStyles);
                        rightSpacing = getHorizontalSpacing(currentKey, nextKey);
                    }
                    return (jsx("div", { className: buttonItemWrapperStyles.join(' ').trim(), style: {
                            flexGrow: isButton && buttonWidth === '100%' ? 1 : undefined,
                            flexBasis: isButton && buttonWidth && buttonWidth !== 'auto'
                                ? `calc(${buttonWidth + rightSpacing})`
                                : undefined,
                        }, children: _renderButton(Object.assign(Object.assign({}, buttonGroupItem), { surface, width: buttonWidth }), idx) }, `wrapper-${idx}`));
                }) }, `row-${chunkIdx}`));
        }) }));
};

export { ButtonGroup as default };
